/**
 * User Detail View Component - Detachable Admin Module
 * 
 * This component provides detailed view of a user with credit management and plan management.
 * When extracting to a separate app, this component can be moved with minimal changes.
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import ChangePlanModal from './ChangePlanModal';
import UpdateLimitsModal from './UpdateLimitsModal';
import { useAdminData } from '../hooks/useAdminData';
import { 
  formatCredits, 
  formatCurrency,
  getPlanInfo, 
  formatUserStatus, 
  getUserDisplayName, 
  getUserInitials,
  formatDate,
  formatRelativeTime,
  getCreditTypeInfo,
  calculateCreditUsage,
  getCreditUsageColor,
  formatRemainingTotalCredits
} from '../utils';
import type { CreditType, PlanType, PlatformUser } from '../types';

/**
 * Detailed user view with credit management and plan management
 */
export const UserDetailView: React.FC = () => {
  const { userIdentifier: rawUserIdentifier } = useParams<{ userIdentifier: string }>();
  const userIdentifier = rawUserIdentifier ? decodeURIComponent(rawUserIdentifier) : undefined;
  const navigate = useNavigate();
  const { credits, projects, operations, billingConfig } = useAdminData();

  // UI state
  const [activeTab, setActiveTab] = useState<'overview' | 'credits' | 'projects'>('overview');
  const [showAddCredits, setShowAddCredits] = useState(false);
  const [showChangePlan, setShowChangePlan] = useState(false);
  const [showUpdateLimits, setShowUpdateLimits] = useState(false);
  
  // Form state
  const [creditForm, setCreditForm] = useState({
    amount: '',
    creditType: 'free_platform' as CreditType,
    description: ''
  });

  useEffect(() => {
    if (userIdentifier) {
      credits.loadCredits(userIdentifier);
      projects.loadProjects(userIdentifier);
    }
  }, [userIdentifier]);

  useEffect(() => {
    // Load billing configuration when component mounts
    billingConfig.loadConfig();
  }, []);

  const handleAddCredits = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userIdentifier) return;

    const amount = parseFloat(creditForm.amount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid credit amount');
      return;
    }

    const success = await operations.addCredits(
      userIdentifier,
      amount,
      creditForm.creditType,
      creditForm.description || undefined
    );

    if (success) {
      setShowAddCredits(false);
      setCreditForm({ amount: '', creditType: 'free_platform' as CreditType, description: '' });
      // Refresh data
      credits.refreshCredits();
    }
  };

  const handleChangePlanSuccess = () => {
    setShowChangePlan(false);
    // Refresh data
    credits.refreshCredits();
  };

  const handleUpdateLimitsSuccess = () => {
    setShowUpdateLimits(false);
    // Refresh data
    credits.refreshCredits();
  };

  if (!userIdentifier) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">User Not Found</h3>
          <p className="text-gray-600 mb-4">The requested user could not be found.</p>
          <Link
            to="/admin"
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            Back to Users
          </Link>
        </div>
      </AdminLayout>
    );
  }

  const user = credits.credits?.user;
  const creditSummary = credits.credits?.credit_summary;
  // Separate active and exhausted entries
  const totalCredits = Number(creditSummary?.total_available_credits) || 0;
  const allEntries = credits.credits?.credit_entries || [];
  
  // If total balance is negative, all entries are considered exhausted
  const activeEntries = totalCredits <= 0 ? [] : allEntries.filter(entry => entry.remaining_amount > 0);
  const exhaustedEntries = totalCredits <= 0 ? allEntries : allEntries.filter(entry => entry.remaining_amount <= 0);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Link
            to="/admin"
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            ← Back
          </Link>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowAddCredits(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700"
            >
              Add Credits
            </button>
            <button
              onClick={() => setShowChangePlan(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded hover:bg-purple-700"
            >
              Change Plan
            </button>
            {creditSummary && creditSummary.plan_type === 'monthly' && (
              <button
                onClick={() => setShowUpdateLimits(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
              >
                Update Limits
              </button>
            )}
          </div>
        </div>

        {/* Loading State */}
        {credits.loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading user details...</p>
          </div>
        )}

        {/* Error State */}
        {credits.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <span className="text-red-400">⚠️</span>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error Loading User</h3>
                <p className="mt-1 text-sm text-red-700">{credits.error}</p>
                <button
                  onClick={() => credits.refreshCredits()}
                  className="mt-2 text-sm text-red-600 hover:text-red-500 font-medium"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* User Content */}
        {creditSummary && (
          <>
            {/* User Profile Card */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="h-16 w-16 rounded-full bg-blue-600 flex items-center justify-center text-white text-xl font-medium">
                    {user ? getUserInitials(user) : 'U'}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {user ? getUserDisplayName(user) : 'Unknown User'}
                    </h3>
                    {user && (
                      <div className="mt-2">
                        {(() => {
                          const status = formatUserStatus(user as PlatformUser);
                          return (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                              {status.text}
                            </span>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="text-right">
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    getPlanInfo(creditSummary.plan_type).color
                  }`}>
                    {getPlanInfo(creditSummary.plan_type).label}
                  </div>
                  <div className="flex items-center gap-6 mt-2">
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${
                        (Number(creditSummary?.total_lifetime_usage) || 0) < 0
                          ? 'text-red-600'
                          : 'text-gray-900'
                      }`}>
                        {formatCurrency(Number(creditSummary.total_lifetime_usage) || 0)}
                      </p>
                      <p className="text-sm text-gray-500">
                        Lifetime Usage
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${
                        creditSummary?.unlimited_access
                          ? 'text-purple-600'
                          : (Number(creditSummary?.total_available_credits) || 0) < 0
                          ? 'text-red-600'
                          : 'text-gray-900'
                      }`}>
                        {creditSummary?.unlimited_access 
                          ? 'Unlimited' 
                          : formatCurrency(Number(creditSummary.total_available_credits) || 0)
                        }
                      </p>
                      <p className="text-sm text-gray-500">
                        {creditSummary?.unlimited_access ? 'Unlimited Access' : 'Total Credits'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8 px-6">
                  {[
                    { id: 'overview', label: 'Overview' },
                    { id: 'credits', label: 'Credit History' },
                    { id: 'projects', label: 'Projects' }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === tab.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    {/* Credit Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {(() => {
                        // Filter credit types based on user's plan
                        const allCredits = [
                          { 
                            type: 'free_platform', 
                            amount: Number(creditSummary.free_platform_credits) || 0,
                            label: 'Free Credits'
                          },
                          { 
                            type: 'monthly', 
                            amount: Number(creditSummary.monthly_credits) || 0,
                            label: 'Monthly Credits'
                          },
                          { 
                            type: 'paid_platform', 
                            amount: Number(creditSummary.paid_platform_credits) || 0,
                            label: 'Paid Credits'
                          }
                        ];
                        
                        // Filter based on plan type
                        const planType = creditSummary?.plan_type?.toLowerCase();
                        return allCredits.filter(credit => {
                          // Always show free_platform and paid_platform credits
                          if (credit.type === 'free_platform' || credit.type === 'paid_platform') {
                            return true;
                          }
                          // Only show monthly credits for monthly plan users
                          if (credit.type === 'monthly') {
                            return planType === 'monthly';
                          }
                          return false;
                        });
                      })().map((credit) => {
                        const typeInfo = getCreditTypeInfo(credit.type);
                        const remainingTotalFormat = formatRemainingTotalCredits(allEntries, credit.type, creditSummary);
                        return (
                          <div key={credit.type} className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-gray-600">{credit.label}</p>
                                <p className="text-lg font-semibold text-gray-900">
                                  {remainingTotalFormat}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  Available / Total
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Lifetime Usage</p>
                            <p className="text-lg font-semibold text-gray-900">
                              {formatCurrency(Number(creditSummary.total_lifetime_usage) || 0)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Plan Information */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Plan Details</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-sm text-gray-600">Current Plan</p>
                          <p className="text-lg font-medium text-gray-900">
                            {getPlanInfo(creditSummary.plan_type).label}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Monthly Allowance</p>
                          <p className="text-lg font-medium text-gray-900">
                            {formatCredits(Number(creditSummary.monthly_allowance) || 0)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Credits Tab */}
                {activeTab === 'credits' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-600">
                        {activeEntries.length} active, {' '}
                        {exhaustedEntries.length} exhausted
                      </p>
                    </div>
                    
                    <div className="space-y-4">
                      {/* Active Entries */}
                      {activeEntries.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-gray-900 mb-2">Active Entries</h5>
                          <div className="space-y-2 max-h-60 overflow-y-auto">
                            {activeEntries.map((entry) => {
                              const typeInfo = getCreditTypeInfo(entry.type);
                              
                              return (
                                <div key={entry.id} className="border rounded-lg p-3 hover:bg-gray-50 transition-colors">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                      <div className="flex-shrink-0">
                                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                        </div>
                                      </div>
                                      <div>
                                        <div className="font-bold text-gray-900 text-sm">
                                          {typeInfo.label} Credits
                                        </div>
                                        <div className="text-xs text-gray-600 mt-1">
                                          Source: {entry.source === 'admin_grant' ? 'Admin Grant' :
                                                  entry.source === 'monthly_allocation' ? 'Monthly Allocation' :
                                                  entry.source === 'signup_bonus' ? 'Welcome Bonus' :
                                                  entry.source || 'System'}
                                          {entry.description && ` • ${entry.description}`}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                          Created {formatRelativeTime(entry.created_at)}
                                          {entry.expires_at && ` • Expires ${formatRelativeTime(entry.expires_at)}`}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <div className="text-sm font-semibold text-gray-900">
                                        {formatCurrency(Number(entry.remaining_amount) || 0)}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        of {formatCurrency(Number(entry.original_amount) || 0)}
                                      </div>
                                      {Number(entry.remaining_amount) < Number(entry.original_amount) && (
                                        <div className="text-xs text-blue-600 mt-1">
                                          {Math.round((Number(entry.remaining_amount) / Number(entry.original_amount)) * 100)}% remaining
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Exhausted Entries */}
                      {exhaustedEntries.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-gray-900 mb-2">Exhausted Entries</h5>
                          <div className="space-y-2 max-h-60 overflow-y-auto">
                            {exhaustedEntries.map((entry) => {
                              const typeInfo = getCreditTypeInfo(entry.type);
                              
                              return (
                                <div key={entry.id} className="border rounded-lg p-3 hover:bg-gray-50 transition-colors bg-gray-50">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                      <div className="flex-shrink-0">
                                        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                                          <div className="w-2 h-2 rounded-full bg-red-500"></div>
                                        </div>
                                      </div>
                                      <div>
                                        <div className="font-bold text-gray-900 text-sm flex items-center">
                                          {typeInfo.label} Credits
                                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                            Exhausted
                                          </span>
                                        </div>
                                        <div className="text-xs text-gray-600 mt-1">
                                          Source: {entry.source === 'admin_grant' ? 'Admin Grant' :
                                                  entry.source === 'monthly_allocation' ? 'Monthly Allocation' :
                                                  entry.source === 'signup_bonus' ? 'Welcome Bonus' :
                                                  entry.source || 'System'}
                                          {entry.description && ` • ${entry.description}`}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                          Created {formatRelativeTime(entry.created_at)}
                                          {entry.expires_at && ` • Expires ${formatRelativeTime(entry.expires_at)}`}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <div className={`text-sm font-semibold ${
                                        entry.remaining_amount < 0 ? 'text-red-600' : 'text-gray-500'
                                      }`}>
                                        {/* Show $0.000000 for entries exhausted due to negative total balance */}
                                        {totalCredits <= 0 && entry.remaining_amount > 0 
                                          ? formatCurrency(0) 
                                          : formatCurrency(Number(entry.remaining_amount) || 0)
                                        }
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        of {formatCurrency(Number(entry.original_amount) || 0)}
                                      </div>
                                      {entry.remaining_amount < 0 ? (
                                        <div className="text-xs text-red-600 mt-1">
                                          Exhausted
                                        </div>
                                      ) : totalCredits <= 0 && entry.remaining_amount > 0 ? (
                                        <div className="text-xs text-red-600 mt-1">
                                          Exhausted due to negative balance
                                        </div>
                                      ) : (
                                        <div className="text-xs text-gray-500 mt-1">
                                          Fully consumed
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* No entries message */}
                      {activeEntries.length === 0 && exhaustedEntries.length === 0 && (
                        <p className="text-center text-gray-500 py-8">No credit entries found</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Projects Tab */}
                {activeTab === 'projects' && (
                  <div className="space-y-4">
                    {projects.loading ? (
                      <div className="text-center py-8">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <p className="mt-2 text-sm text-gray-600">Loading projects...</p>
                      </div>
                    ) : projects.error ? (
                      <div className="text-center py-8">
                        <p className="text-red-600">{projects.error}</p>
                      </div>
                    ) : projects.projects && projects.projects.projects.length > 0 ? (
                      <>
                        <div className="flex items-center justify-between mb-4">
                          <p className="text-sm text-gray-600">
                            Total: {projects.projects.total_projects} {projects.projects.total_projects === 1 ? 'project' : 'projects'}
                          </p>
                        </div>

                        <div className="overflow-x-auto">
                          <table className="w-full divide-y divide-gray-200" style={{tableLayout: 'fixed', width: '100%'}}>
                            <colgroup>
                              <col style={{width: '25%'}} />
                              <col style={{width: '30%'}} />
                              <col style={{width: '12%'}} />
                              <col style={{width: '12%'}} />
                              <col style={{width: '18%'}} />
                              <col style={{width: '12%'}} />
                            </colgroup>
                            <thead className="bg-gray-50">
                              <tr>
                                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Project Name
                                </th>
                                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Description
                                </th>
                                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Status
                                </th>
                                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Type
                                </th>
                                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Created Date
                                </th>
                                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Total Cost
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {projects.projects.projects.map((project) => {
                                // Format date from epoch timestamp to MM-DD-YYYY
                                const date = new Date(project.created_at * 1000);
                                const formattedDate = `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}-${date.getFullYear()}`;

                                return (
                                  <tr key={project.id} className="hover:bg-gray-50">
                                    <td className="px-3 py-3">
                                      <div className="text-sm font-medium text-gray-900 overflow-hidden whitespace-nowrap" style={{textOverflow: 'ellipsis'}} title={project.title}>
                                        <Link to={`${project.project_type === 'FedRamp' ? '/fedramp/chat' : '/chat'}/${project.id}`} target='_blank'>{project.title}</Link>
                                      </div>
                                    </td>
                                    <td className="px-3 py-3">
                                      <div className="text-sm text-gray-500 overflow-hidden whitespace-nowrap" style={{textOverflow: 'ellipsis'}} title={project.description}>
                                        {project.description || '-'}
                                      </div>
                                    </td>
                                    <td className="px-3 py-3 whitespace-nowrap">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                        project?.is_deleted
                                          ? 'bg-red-100 text-red-800'
                                          : 'bg-green-100 text-green-800'
                                      }`}>
                                        {project?.is_deleted ? 'Inactive' : 'Active'}
                                      </span>
                                    </td>
                                    <td className="px-3 py-3 whitespace-nowrap">
                                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                        project.project_type === 'Web'
                                          ? 'bg-blue-100 text-blue-800'
                                          : 'bg-purple-100 text-purple-800'
                                      }`}>
                                        {project.project_type}
                                      </span>
                                    </td>
                                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                                      {formattedDate}
                                    </td>
                                    <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                      {formatCurrency(project.total_cost)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-gray-500">No projects found for this user</p>
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>
          </>
        )}

        {/* Add Credits Modal */}
        {showAddCredits && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Add Credits</h3>
                
                <form onSubmit={handleAddCredits} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Credit Amount ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max="10000"
                      value={creditForm.amount}
                      onChange={(e) => setCreditForm(prev => ({ ...prev, amount: e.target.value }))}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Credit Type
                    </label>
                    <select
                      value={creditForm.creditType}
                      onChange={(e) => setCreditForm(prev => ({ ...prev, creditType: e.target.value as CreditType }))}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="free_platform">Free Platform Credits</option>
                      <option value="monthly">Monthly Credits</option>
                      <option value="paid_platform">Paid Platform Credits</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description (Optional)
                    </label>
                    <textarea
                      value={creditForm.description}
                      onChange={(e) => setCreditForm(prev => ({ ...prev, description: e.target.value }))}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      rows={3}
                      placeholder="Reason for credit grant..."
                    />
                  </div>
                  
                  {operations.error && (
                    <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                      {operations.error}
                    </div>
                  )}
                  
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddCredits(false);
                        operations.clearError();
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={operations.loading}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      {operations.loading ? 'Adding...' : 'Add Credits'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Change Plan Modal */}
        <ChangePlanModal
          isOpen={showChangePlan}
          onClose={() => setShowChangePlan(false)}
          onSuccess={handleChangePlanSuccess}
          userIdentifier={userIdentifier || ''}
          currentPlan={(creditSummary?.plan_type as PlanType) || 'starter' as PlanType}
          currentWeeklyAllowance={undefined}
          currentMonthlyAllowance={creditSummary?.monthly_allowance}
          userDisplayName={user ? getUserDisplayName(user) : undefined}
        />

        {/* Update Limits Modal */}
        <UpdateLimitsModal
          isOpen={showUpdateLimits}
          onClose={() => setShowUpdateLimits(false)}
          onSuccess={handleUpdateLimitsSuccess}
          userIdentifier={userIdentifier || ''}
          currentPlan={(creditSummary?.plan_type as PlanType) || 'starter' as PlanType}
          currentWeeklyAllowance={undefined}
          currentMonthlyAllowance={creditSummary?.monthly_allowance}
          userDisplayName={user ? getUserDisplayName(user) : undefined}
        />
      </div>
    </AdminLayout>
  );
};

export default UserDetailView;
